import React from "react";
import { useApp } from "./store.jsx";
import { api } from "./api.jsx";

const { Icon } = window.CardboardmarketDesignSystem_2a3239;

/*
 * A MODULE OF ITS OWN, because both the draw's page and the reveal show it.
 *
 * It started inside campaign.jsx and reveal.jsx imported it from there — which
 * closed a circle, since campaign.jsx already imports DrawBanner from
 * reveal.jsx. The symptom was the control silently not rendering: with an ES
 * module cycle one side evaluates first and the other sees `undefined`, and
 * React renders nothing rather than complaining. A third module both can
 * depend on cannot be made circular by anybody later.
 */
/**
 * Picking which one, after winning.
 *
 * THE RESULT IS ALREADY SETTLED and this does not change it. Everyone wins
 * what the campaign says — "a booster box" — and this is which box. The
 * owner's description: "they generally speaking win a booster box, but they
 * pick the specific one later."
 *
 * NOTHING IS RESERVED, so there is no count, no sold-out state and no race to
 * lose: two winners taking the same option was stated as fine. That is why
 * pressing one of these is instant and never refused for being too late to a
 * popular one.
 *
 * CHANGEABLE UNTIL IT IS HANDED OVER, because somebody who pressed in the
 * first excited second of the confetti should be able to think again.
 */
export function PrizeChoice({ campaign }) {
  const { loadCampaigns, t } = useApp();
  const [saving, setSaving] = React.useState(null);
  const [problem, setProblem] = React.useState(null);

  /*
   * TWO MODES, ONE LIST.
   *
   * Everybody sees what is on offer; only a winner can press one. It shipped
   * as the winner's control alone, and an operator who had just typed six
   * boxes into the back office then found them NOWHERE on the page — not
   * before the draw, not to anybody who had not won. A pool is the answer to
   * "what could I win", which is the question the page exists for, so it
   * cannot be locked behind winning it.
   *
   * A second read-only list beside this one was the obvious build and the
   * wrong one: two renderings of the same rows drift, and the one nobody is
   * logged in to test is the one that drifts first.
   */
  const won = campaign.youWon;
  if (!campaign.prizes || campaign.prizes.length === 0) return null;

  const chosen = won ? won.prizeId : null;
  const settled = Boolean(won && won.claimedAt);
  const canChoose = Boolean(won) && !settled;

  const choose = async (prizeId) => {
    if (settled || prizeId === chosen) return;
    setSaving(prizeId); setProblem(null);
    try {
      await api.chooseCampaignPrize(campaign.id, prizeId);
      await loadCampaigns();
    } catch (e) {
      setProblem(t("We could not save that. Try again in a moment."));
    } finally { setSaving(null); }
  };

  return (
    <div className="cbm-stack" style={{ gap: 'var(--space-3)' }}>
      <span className="cbm-label">
        {settled ? t("What you chose")
          : chosen ? t("Your choice")
          : canChoose ? t("Which one would you like?")
          : t("You pick one of these")}
      </span>
      {problem ? <span style={{ font: 'var(--type-body-sm)', color: 'var(--status-danger)' }}>{problem}</span> : null}
      <div className="cbm-stack" style={{ gap: 'var(--space-2)' }}>
        {campaign.prizes.map((p) => {
          const isChosen = p.id === chosen;
          /* A settled choice shows only itself: the other options are no
             longer offers, and listing them beside a prize already posted
             invites somebody to press one. */
          if (settled && !isChosen) return null;
          /* A row nobody can press is not a button. Rendering one disabled
             would put it in the tab order for a screen reader to announce as
             an unavailable control, when it is simply a line of text. */
          const Row = canChoose ? 'button' : 'div';
          return (
            <Row
              key={p.id}
              type={canChoose ? 'button' : undefined}
              data-cbm="prize-option"
              disabled={canChoose ? saving !== null : undefined}
              onClick={canChoose ? () => choose(p.id) : undefined}
              aria-pressed={canChoose || isChosen ? isChosen : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-3)', width: '100%',
                textAlign: 'left', cursor: canChoose ? 'pointer' : 'default',
                padding: 'var(--space-4)',
                border: `1px solid ${isChosen ? 'var(--ink-900)' : 'var(--border-hairline)'}`,
                background: isChosen ? 'var(--ink-900)' : 'var(--white)',
                color: isChosen ? 'var(--kraft-50)' : 'var(--text-body)',
                borderRadius: 'var(--radius-sm)',
                font: 'var(--type-ui)', fontSize: 'var(--text-sm)',
                transition: 'var(--transition-control)',
              }}
            >
              {/* The note is where an operator puts what tells two similar
                  boxes apart, so it has to render — it was collected and then
                  never shown anywhere. */}
              <span className="cbm-stack" style={{ flex: 1, gap: 2 }}>
                <span>{p.name}</span>
                {p.note ? (
                  <span style={{
                    font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)',
                    color: isChosen ? 'var(--kraft-200)' : 'var(--text-muted)',
                  }}>{p.note}</span>
                ) : null}
              </span>
              {isChosen ? <Icon name="check" size={16} /> : null}
              {saving === p.id ? <span className="cbm-meta">{t("Saving…")}</span> : null}
            </Row>
          );
        })}
      </div>
      {canChoose ? (
        <span style={{ font: 'var(--type-body-sm)', fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
          {chosen ? t("You can change this until we hand it over.") : t("You can change your mind later.")}
        </span>
      ) : null}
    </div>
  );
}
